import React, { useState } from "react";
import { Plus, Boxes } from "lucide-react";
import { Button } from "@shared/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@shared/ui/alert-dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { GenericTable } from "@shared/components/GenericTable";
import { useSopTabDetails, useDeleteSopMaterial } from "../../../../hooks";
import { AddMaterialModal, EditMaterialModal } from "../modals/MaterialModal";
import { useToast } from "@shared/hooks/use-toast";
import { useCurrency } from "@shared/selectors/globalSelectors";

interface MaterialsTabProps {
  sopDetails: any;
}

interface Material {
  name: string;
  size?: string;
  quantity_required: string;
  cost: string;
  quantity: string;
}

const MaterialsTab: React.FC<MaterialsTabProps> = ({ sopDetails }) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(
    null
  );
  const [selectedMaterialIndex, setSelectedMaterialIndex] =
    useState<number>(-1);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [materialToDelete, setMaterialToDelete] = useState<{
    index: number;
    material: Material;
  } | null>(null);

  const deleteMaterialMutation = useDeleteSopMaterial();
  const { toast } = useToast();
  const currency = useCurrency();

  // Fetch material data using the hook
  const {
    data: materialData,
    isLoading: loading,
    error: materialsError,
  } = useSopTabDetails({
    id: sopDetails.id.toString(),
    remoteUtilityId: sopDetails.remoteUtilityId?.toString(),
    fieldName: "material",
  });

  // Event handlers
  const handleAddMaterial = () => {
    setIsAddModalOpen(true);
  };

  const handleEditMaterial = (materialIndex: number, material: Material) => {
    setSelectedMaterial(material);
    setSelectedMaterialIndex(materialIndex);
    setIsEditModalOpen(true);
  };

  const handleDeleteMaterial = (materialIndex: number, material: Material) => {
    setMaterialToDelete({ index: materialIndex, material });
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteMaterial = async () => {
    if (!materialToDelete) return;

    try {
      await deleteMaterialMutation.mutateAsync({
        sopId: sopDetails.id.toString(),
        materialName: materialToDelete.material.name,
      });

      setDeleteConfirmOpen(false);
      setMaterialToDelete(null);
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedMaterial(null);
    setSelectedMaterialIndex(-1);
  };

  // Extract materials from API response and add index for row identification
  const materials: (Material & { index: number })[] = (
    materialData?.result?.material || []
  ).map((material: Material, index: number) => ({
    ...material,
    index,
  }));

  // GenericTable column configuration
  const tableColumns = [
    {
      key: "index",
      header: "Sr. No",
      render: (value: number) => (
        <span className="font-medium">{value + 1}</span>
      ),
    },
    {
      key: "name",
      header: "Material Name",
      sortable: true,
      render: (value: string) => <span className="text-gray-900">{value}</span>,
    },
    {
      key: "size",
      header: "Size",
      render: (value: string) => (
        <span className="text-gray-700">{value || "-"}</span>
      ),
    },
    {
      key: "quantity",
      header: "Quantity",
      sortable: true,
      render: (value: string) => (
        <span className="text-gray-900">{value || 0}</span>
      ),
    },
    {
      key: "cost",
      header: "Cost / Unit",
      sortable: true,
      render: (value: string) => (
        <span className="text-gray-900 font-medium">{currency} {value || "0"}</span>
      ),
    },
  ];

  // GenericTable actions configuration
  const tableActions = [
    {
      label: "Edit Material",
      onClick: (row: Material & { index: number }) =>
        handleEditMaterial(row.index, row),
      icon: "Pencil" as const,
      variant: "ghost" as const,
    },
    {
      label: "Delete Material",
      onClick: (row: Material & { index: number }) =>
        handleDeleteMaterial(row.index, row),
      icon: "Trash2" as const,
      variant: "ghost" as const,
      className: "text-destructive hover:text-destructive",
    },
  ];

  return (
    <>
      <div className="mt-4 space-y-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center pb-2">
                <Boxes className="mr-2 h-5 w-5 text-primary" />
                Required Materials & Equipment
              </CardTitle>
              <CardDescription>Materials needed for this SOP</CardDescription>
            </div>
            <Button onClick={handleAddMaterial}>
              <Plus className="mr-2 h-4 w-4" />
              Add
            </Button>
          </CardHeader>
          <CardContent>
            <GenericTable
              data={materials}
              columns={tableColumns}
              rowKey="index"
              actions={tableActions}
              actionsType="dropdown"
              emptyMessage="No materials defined for this SOP"
            />
          </CardContent>
        </Card>
      </div>

      {/* Add Material Modal */}
      <AddMaterialModal
        isOpen={isAddModalOpen}
        onClose={handleCloseAddModal}
        sopId={sopDetails.id.toString()}
      />

      {/* Edit Material Modal */}
      <EditMaterialModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        sopId={sopDetails.id.toString()}
        material={selectedMaterial}
        materialIndex={selectedMaterialIndex}
      />
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Material</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the material "
              {materialToDelete?.material.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteMaterial}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMaterialMutation.isPending}
            >
              {deleteMaterialMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default MaterialsTab;
